import React, { useState } from 'react';
import styled from 'styled-components';
import { useInventory } from '../../contexts/InventoryContext';

const Container = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const Header = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const FilterBar = styled.div`
  padding: 20px;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  gap: 15px;
  align-items: center;
  flex-wrap: wrap;
`;

const FilterSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  font-size: 14px;
  background: white;

  &:focus {
    outline: none;
    border-color: #667eea;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }

  option {
    background: white;
    color: #333;
  }
`;

const TimelineContainer = styled.div`
  padding: 20px;
  max-height: 600px;
  overflow-y: auto;
  
  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(79, 172, 254, 0.3);
    border-radius: 3px;
    
    &:hover {
      background: rgba(79, 172, 254, 0.5);
    }
  }
`;

const TimelineItem = styled.div`
  display: flex;
  margin-bottom: 24px;
  position: relative;

  &:not(:last-child)::after {
    content: '';
    position: absolute;
    left: 19px;
    top: 40px;
    bottom: -24px;
    width: 2px;
    background: #e9ecef;
  }

  &:last-child {
    margin-bottom: 0;
  }
`;

const TimelineIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: bold;
  margin-right: 16px;
  flex-shrink: 0;
  position: relative;
  z-index: 1;
  background: ${props => {
    switch (props.type) {
      case 'deposit': return 'linear-gradient(135deg, #28a745, #20c997)';
      case 'withdrawal': return 'linear-gradient(135deg, #dc3545, #e83e8c)';
      case 'transfer': return 'linear-gradient(135deg, #17a2b8, #6f42c1)';
      case 'mint': return 'linear-gradient(135deg, #ffc107, #fd7e14)';
      case 'burn': return 'linear-gradient(135deg, #6c757d, #495057)';
      case 'quality_check': return 'linear-gradient(135deg, #007bff, #6610f2)';
      default: return 'linear-gradient(135deg, #6c757d, #495057)';
    }
  }};
  color: white;
`;

const TimelineContent = styled.div`
  flex: 1;
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 16px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: -8px;
    top: 12px;
    width: 0;
    height: 0;
    border: 8px solid transparent;
    border-right-color: #e9ecef;
  }

  &::after {
    content: '';
    position: absolute;
    left: -7px;
    top: 12px;
    width: 0;
    height: 0;
    border: 8px solid transparent;
    border-right-color: white;
  }
`;

const MovementHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
`;

const MovementTitle = styled.h4`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #2c3e50;
`;

const MovementTime = styled.div`
  font-size: 12px;
  color: #6c757d;
  display: flex;
  flex-direction: column;
  text-align: right;
`;

const MovementDescription = styled.div`
  font-size: 14px;
  color: #495057;
  margin-bottom: 12px;
  line-height: 1.5;
`;

const MovementDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 12px;
  margin-top: 12px;
`;

const DetailItem = styled.div`
  font-size: 12px;
`;

const DetailLabel = styled.div`
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 2px;
`;

const DetailValue = styled.div`
  font-weight: 600;
  color: #495057;
`;

const StatusBadge = styled.span`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${props => {
    switch (props.status) {
      case 'completed': return '#d4edda';
      case 'pending': return '#fff3cd';
      case 'failed': return '#f8d7da';
      case 'in_progress': return '#d1ecf1';
      default: return '#e9ecef';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'completed': return '#155724';
      case 'pending': return '#856404';
      case 'failed': return '#721c24';
      case 'in_progress': return '#0c5460';
      default: return '#495057';
    }
  }};
`;

const EmptyState = styled.div`
  padding: 60px 20px;
  text-align: center;
  color: #6c757d;
  
  .icon {
    font-size: 48px;
    margin-bottom: 16px;
  }
  
  .title {
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 8px;
  }
  
  .description {
    font-size: 14px;
  }
`;

const MovementHistory = () => {
  const { movements } = useInventory();
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  // Mock movements data if not available
  const mockMovements = [
    {
      id: 'MV001',
      type: 'deposit',
      title: 'Sugar Deposit',
      description: 'New sugar batch deposited at Miami Warehouse',
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
      status: 'completed',
      itemId: 'SUGAR-001',
      quantity: 500,
      unit: 'tons',
      fromLocation: 'Transport Truck #123',
      toLocation: 'Miami Warehouse - Zone A',
      operator: 'John Smith',
      transactionHash: `cb${Math.floor(Math.random() * 100).toString().padStart(2, '0')}${Math.random().toString(16).substr(2, 40)}`,
      tokensMinted: 750000
    },
    {
      id: 'MV002',
      type: 'mint',
      title: 'Token Minting',
      description: 'Minted 750,000 PING tokens for sugar deposit',
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000 + 5 * 60 * 1000).toISOString(),
      status: 'completed',
      itemId: 'SUGAR-001',
      quantity: 750000,
      unit: 'PING',
      operator: 'System',
      transactionHash: `cb${Math.floor(Math.random() * 100).toString().padStart(2, '0')}${Math.random().toString(16).substr(2, 40)}`
    },
    {
      id: 'MV003',
      type: 'quality_check',
      title: 'Quality Verification',
      description: 'Quality inspection completed for Coffee batch',
      timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000).toISOString(),
      status: 'completed',
      itemId: 'COFFEE-002',
      grade: 'Arabica Premium',
      certificates: 'Organic, Fair Trade',
      inspector: 'Maria Garcia',
      qualityScore: 9.2
    },
    {
      id: 'MV004',
      type: 'transfer',
      title: 'Warehouse Transfer',
      description: 'Gold bars transferred between vault sections',
      timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000).toISOString(),
      status: 'in_progress',
      itemId: 'GOLD-003',
      quantity: 50,
      unit: 'oz',
      fromLocation: 'Vault A-1',
      toLocation: 'Vault B-2',
      operator: 'Security Team',
      estimatedCompletion: new Date(Date.now() + 1 * 60 * 60 * 1000).toISOString(),
      transactionHash: `cb${Math.floor(Math.random() * 100).toString().padStart(2, '0')}${Math.random().toString(16).substr(2, 40)}`
    },
    {
      id: 'MV005',
      type: 'withdrawal',
      title: 'Silver Withdrawal',
      description: 'Silver bars withdrawn for client delivery',
      timestamp: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
      status: 'completed',
      itemId: 'SILVER-004',
      quantity: 200,
      unit: 'oz',
      fromLocation: 'Chicago Warehouse',
      toLocation: 'Client Pickup',
      operator: 'David Wilson',
      transactionHash: `cb${Math.floor(Math.random() * 100).toString().padStart(2, '0')}${Math.random().toString(16).substr(2, 40)}`,
      tokensBurned: 5000
    },
    {
      id: 'MV006',
      type: 'burn',
      title: 'Token Burning',
      description: 'Burned 5,000 PING tokens for silver withdrawal',
      timestamp: new Date(Date.now() - 8 * 60 * 60 * 1000 + 10 * 60 * 1000).toISOString(),
      status: 'completed',
      itemId: 'SILVER-004',
      quantity: 5000,
      unit: 'PING',
      operator: 'System',
      transactionHash: `cb${Math.floor(Math.random() * 100).toString().padStart(2, '0')}${Math.random().toString(16).substr(2, 40)}`
    }
  ];

  const allMovements = movements?.length > 0 ? movements : mockMovements;

  // Filter movements
  const filteredMovements = allMovements.filter(movement => {
    const matchesType = filterType === 'all' || movement.type === filterType;
    const matchesStatus = filterStatus === 'all' || movement.status === filterStatus;
    return matchesType && matchesStatus;
  });

  const getMovementIcon = (type) => {
    switch (type) {
      case 'deposit': return '📥';
      case 'withdrawal': return '📤';
      case 'transfer': return '🔄';
      case 'mint': return '🪙';
      case 'burn': return '🔥';
      case 'quality_check': return '🔍';
      default: return '📋';
    }
  };

  const formatDateTime = (timestamp) => {
    const date = new Date(timestamp);
    return {
      date: date.toLocaleDateString(),
      time: date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    };
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  return (
    <Container>
      <Header>
        <Title>
          📜 Movement History
        </Title>
      </Header>

      <FilterBar>
        <FilterSelect
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
        >
          <option value="all">All Movement Types</option>
          <option value="deposit">Deposits</option>
          <option value="withdrawal">Withdrawals</option>
          <option value="transfer">Transfers</option>
          <option value="mint">Token Minting</option>
          <option value="burn">Token Burning</option>
          <option value="quality_check">Quality Checks</option>
        </FilterSelect>

        <FilterSelect
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="all">All Statuses</option>
          <option value="completed">Completed</option>
          <option value="in_progress">In Progress</option>
          <option value="pending">Pending</option>
          <option value="failed">Failed</option>
        </FilterSelect>
      </FilterBar>

      <TimelineContainer>
        {filteredMovements.length > 0 ? (
          filteredMovements.map(movement => {
            const dateTime = formatDateTime(movement.timestamp);
            
            return (
              <TimelineItem key={movement.id}>
                <TimelineIcon type={movement.type}>
                  {getMovementIcon(movement.type)}
                </TimelineIcon>
                
                <TimelineContent>
                  <MovementHeader>
                    <div>
                      <MovementTitle>{movement.title}</MovementTitle>
                      <StatusBadge status={movement.status}>
                        {movement.status.replace('_', ' ')}
                      </StatusBadge>
                    </div>
                    <MovementTime>
                      <div>{dateTime.date}</div>
                      <div>{dateTime.time}</div>
                    </MovementTime>
                  </MovementHeader>

                  <MovementDescription>
                    {movement.description}
                  </MovementDescription>

                  <MovementDetails>
                    <DetailItem>
                      <DetailLabel>Movement ID</DetailLabel>
                      <DetailValue>{movement.id}</DetailValue>
                    </DetailItem>

                    {movement.itemId && (
                      <DetailItem>
                        <DetailLabel>Item ID</DetailLabel>
                        <DetailValue>{movement.itemId}</DetailValue>
                      </DetailItem>
                    )}

                    {movement.quantity && (
                      <DetailItem>
                        <DetailLabel>Quantity</DetailLabel>
                        <DetailValue>
                          {formatNumber(movement.quantity)} {movement.unit}
                        </DetailValue>
                      </DetailItem>
                    )}

                    {movement.fromLocation && (
                      <DetailItem>
                        <DetailLabel>From</DetailLabel>
                        <DetailValue>{movement.fromLocation}</DetailValue>
                      </DetailItem>
                    )}

                    {movement.toLocation && (
                      <DetailItem>
                        <DetailLabel>To</DetailLabel>
                        <DetailValue>{movement.toLocation}</DetailValue>
                      </DetailItem>
                    )}

                    {movement.operator && (
                      <DetailItem>
                        <DetailLabel>Operator</DetailLabel>
                        <DetailValue>{movement.operator}</DetailValue>
                      </DetailItem>
                    )}

                    {movement.transactionHash && (
                      <DetailItem>
                        <DetailLabel>Transaction Hash</DetailLabel>
                        <DetailValue style={{ fontFamily: 'monospace', fontSize: '11px' }}>
                          {movement.transactionHash}
                        </DetailValue>
                      </DetailItem>
                    )}

                    {movement.tokensMinted && (
                      <DetailItem>
                        <DetailLabel>Tokens Minted</DetailLabel>
                        <DetailValue style={{ color: '#28a745' }}>
                          +{formatNumber(movement.tokensMinted)} PING
                        </DetailValue>
                      </DetailItem>
                    )}

                    {movement.tokensBurned && (
                      <DetailItem>
                        <DetailLabel>Tokens Burned</DetailLabel>
                        <DetailValue style={{ color: '#dc3545' }}>
                          -{formatNumber(movement.tokensBurned)} PING
                        </DetailValue>
                      </DetailItem>
                    )}

                    {movement.grade && (
                      <DetailItem>
                        <DetailLabel>Grade</DetailLabel>
                        <DetailValue>{movement.grade}</DetailValue>
                      </DetailItem>
                    )}

                    {movement.certificates && (
                      <DetailItem>
                        <DetailLabel>Certificates</DetailLabel>
                        <DetailValue>{movement.certificates}</DetailValue>
                      </DetailItem>
                    )}

                    {movement.qualityScore && (
                      <DetailItem>
                        <DetailLabel>Quality Score</DetailLabel>
                        <DetailValue style={{ color: '#28a745' }}>
                          {movement.qualityScore}/10
                        </DetailValue>
                      </DetailItem>
                    )}

                    {movement.estimatedCompletion && movement.status === 'in_progress' && (
                      <DetailItem>
                        <DetailLabel>Est. Completion</DetailLabel>
                        <DetailValue>
                          {formatDateTime(movement.estimatedCompletion).time}
                        </DetailValue>
                      </DetailItem>
                    )}
                  </MovementDetails>
                </TimelineContent>
              </TimelineItem>
            );
          })
        ) : (
          <EmptyState>
            <div className="icon">📜</div>
            <div className="title">No Movement History Found</div>
            <div className="description">
              {filterType !== 'all' || filterStatus !== 'all'
                ? 'Try adjusting your filters to see more movements.'
                : 'Inventory movements will appear here as they occur.'
              }
            </div>
          </EmptyState>
        )}
      </TimelineContainer>
    </Container>
  );
};

export default MovementHistory; 